import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { getActionLabel } from "../../../../../../../helpers/correctiveActions";
import { getInvestigationLabel } from "../../../../../../../helpers/investigationLabel";

/**
 * Escala tipografica de estos bloques, en px explicitos.
 *
 * OJO: no usar los variants `caption`, `subtitle*`, `overline` ni `button`. El
 * tema fija htmlFontSize: 10 pero solo define body1 (14.4px) y body2 (12.8px);
 * los demas caen al default de MUI, que calcula sobre ese 10 y sale inflado
 * (caption termina en 16.5px, mas grande que el texto principal).
 *
 * La sancion o medida aplicada se rinde con body1 (14.4px), asi que todo lo de
 * aqui queda por debajo para que la jerarquia se lea sola.
 */
const SIZES = {
  discardedLabel: 12.8,
  sectionTitle: 12,
  origin: 11,
  reason: 11,
};

/**
 * Origen de un renglon: la investigacion de la que se tomo la propuesta.
 *
 * Se marca renglon por renglon porque en un mismo registro conviven lo que
 * propuso el investigador y lo que agrego el resolutor por su cuenta. Lo que no
 * trae marca no se pinta: es del resolutor.
 */
export const ActionOrigin = ({ action }) => {
  const [t] = useTranslation("report");
  // El nombre de la investigacion se arma con el namespace "information".
  const [tInfo] = useTranslation("information");
  const theme = useTheme();

  const investigation =
    typeof action === "object" ? action?.from_investigation : null;
  if (!investigation) return null;

  const label = getInvestigationLabel(investigation, tInfo);
  if (!label) return null;

  return (
    <Typography
      component="div"
      fontSize={SIZES.origin}
      lineHeight={1.4}
      color={theme.palette.blueGray500.main}
    >
      {`${t("Resolution.from_investigation")}: ${label}`}
    </Typography>
  );
};

/**
 * Linea de contexto de un registro cuando la persona puede tener varios: cual es
 * de cuantos, quien lo creo y su ultima actualizacion. `created_by` lo estampa
 * djaguar al crearlo; los registros anteriores no lo traen y solo muestran fecha.
 */
export const RecordMeta = ({ record, position, total = 1 }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  const { name, role } = record?.created_by || {};
  // Solo fecha ("2026-10-07"): con new Date() se recorreria un dia en Mexico.
  const date = record?.updated_at
    ? record.updated_at.split("-").reverse().join("/")
    : "";

  const parts = [
    total > 1 && t("Resolution.record_position", { position, total }),
    name &&
      t("Resolution.record_created_by", {
        author: role ? `${name} (${role})` : name,
      }),
    date && t("Resolution.record_updated", { date }),
  ].filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <Typography
      component="div"
      fontSize={SIZES.origin}
      lineHeight={1.4}
      color={theme.palette.blueGray500.main}
    >
      {parts.join(" · ")}
    </Typography>
  );
};

/**
 * Propuestas que el resolutor decidio no aplicar, con su motivo y la
 * investigacion de la que salieron. Van visualmente por debajo de lo aplicado:
 * son contexto de la decision, no la decision.
 */
export const DiscardedProposals = ({ discarded = [] }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  if (!Array.isArray(discarded) || discarded.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 0.5,
        ml: 1,
        pl: 1,
        borderLeft: `2px solid ${theme.palette.blueGray100.main}`,
      }}
    >
      <Typography
        component="div"
        fontSize={SIZES.sectionTitle}
        fontWeight={600}
        lineHeight={1.4}
        color={theme.palette.blueGray500.main}
      >
        {`${t("Resolution.discarded_proposals")}:`}
      </Typography>

      <Stack spacing={0.25} mt={0.25}>
        {discarded.map((item, idx) => (
          <Box key={`discarded-${idx}`}>
            <Typography
              component="div"
              fontSize={SIZES.discardedLabel}
              lineHeight={1.4}
              color={theme.palette.blueGray500.main}
              sx={{ textDecoration: "line-through" }}
            >
              {item.text || getActionLabel(item)}
            </Typography>
            {item.reason && (
              <Typography
                component="div"
                fontSize={SIZES.reason}
                lineHeight={1.4}
                fontStyle="italic"
                color={theme.palette.blueGray500.main}
              >
                {item.reason}
              </Typography>
            )}
            <ActionOrigin action={item} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
